import { useState } from 'react';
import { Link } from 'react-router-dom';
import { aboutData, testimonialsData, statsData } from '@/mocks/home';
import { siteConfig } from '@/mocks/navigation';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import JsonLd from '@/components/feature/JsonLd';

const formSubmitAddr = 'https://readdy.ai/api/form/d9qrbk7nahb7382ove50';

const aboutPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteConfig.name}`,
  description: `了解${siteConfig.name}的品牌故事、核心价值与团队使命。`,
  url: 'https://haolianxi.com/about',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haolianxi.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://haolianxi.com/about' },
    ],
  },
};

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteConfig.name,
  url: 'https://haolianxi.com/',
  description: siteConfig.description,
  email: aboutData.contact.email,
  telephone: aboutData.contact.phone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: aboutData.contact.address,
    addressLocality: '北京',
    addressRegion: '北京',
    addressCountry: 'CN',
  },
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormState('success');
      return;
    }
    formData.delete('company_alt');

    try {
      const response = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }

      const code = (parsed?.code as string) || '';

      if (response.ok && code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteConfig.name}`}
        description={`了解${siteConfig.name}的品牌故事、核心价值与团队使命。${siteConfig.description}`}
        keywords="关于我们,品牌故事,团队介绍,联系我们,人脉管理平台"
        canonicalUrl="https://haolianxi.com/about"
        ogTitle={`关于我们 - ${siteConfig.name}`}
        ogDescription={`了解${siteConfig.name}的品牌故事、核心价值与团队使命。`}
        ogUrl="https://haolianxi.com/about"
      />
      <JsonLd data={aboutPageJsonLd} />
      <JsonLd data={organizationJsonLd} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          {/* Breadcrumb */}
          <div className="w-full bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800">关于我们</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                关于我们
              </h1>
            </div>
          </div>

          {/* Brand Story */}
          <section id="story" className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50" aria-labelledby="story-heading">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 md:gap-14 items-center">
                <div className="w-full lg:w-1/2">
                  <div className="relative w-full aspect-[4/3] overflow-hidden rounded-lg bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20modern%20startup%20office%20with%20team%20collaborating%20around%20large%20screen%20displaying%20contact%20management%20interface%2C%20natural%20light%20from%20large%20windows%2C%20minimalist%20interior%20design%2C%20professional%20and%20creative%20atmosphere&width=800&height=600&seq=about-story&orientation=landscape"
                      alt="好联系团队"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <h2 id="story-heading" className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    {aboutData.story.subtitle}
                  </h2>
                  {aboutData.story.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full py-12 px-4 md:px-6 bg-background-100" aria-label="数据概览">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {statsData.map((stat) => (
                  <div key={stat.label} className="text-center p-5">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center text-primary-500">
                      <i className={`${stat.icon} text-2xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">{stat.value}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50" aria-labelledby="values-heading">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 id="values-heading" className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                  核心价值观
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  这些价值观指引着我们的每一个产品决策和每一次用户交互
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {aboutData.values.map((v) => (
                  <div key={v.title} className="text-center p-6 bg-background-50 border border-background-200/70 rounded-lg">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-600">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section id="testimonials" className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100" aria-labelledby="about-testimonials-heading">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 id="about-testimonials-heading" className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                  用户口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  来自真实用户的声音，是我们不断前进的动力
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonialsData.slice(0, 6).map((item) => (
                  <div key={item.id} className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < item.rating ? 'text-primary-500 ri-star-fill' : 'text-background-300 ri-star-fill'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                      &ldquo;{item.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                        <div className="text-xs text-foreground-500">{item.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50" aria-labelledby="contact-heading">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 id="contact-heading" className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  有任何问题或合作意向？我们随时为您提供帮助
                </p>
              </div>
              <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
                <div className="w-full lg:w-2/5">
                  <div className="flex flex-col gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                        <a href={`mailto:${aboutData.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200">
                          {aboutData.contact.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                        <a href={`tel:${aboutData.contact.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200">
                          {aboutData.contact.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                        <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信</h3>
                        <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-3/5">
                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70"
                  >
                    <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-6">发送消息</h3>
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                          <input
                            type="text"
                            id="contact-name"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            type="email"
                            id="contact-email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">主题</label>
                        <input
                          type="text"
                          id="contact-subject"
                          name="subject"
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">消息内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        {formState === 'submitting' ? '发送中...' : '发送消息'}
                      </button>

                      {formState === 'success' && (
                        <p className="text-sm text-accent-600 text-center">消息已成功发送，我们会尽快回复您！</p>
                      )}
                      {formState === 'error' && (
                        <p className="text-sm text-red-500 text-center">{formError}</p>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* FAQPage Schema */}
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '如何免费开始使用好联系？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '您可以直接在好联系官网注册免费账号，无需绑定信用卡。免费版支持管理最多 500 个联系人，包含智能分类和基础提醒功能。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '好联系的数据安全性如何？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '好联系采用银行级加密技术，所有数据在传输和存储过程中全程加密，服务器通过 ISO 27001 安全认证。',
                  },
                },
              ],
            }}
          />
        </main>
        <Footer />
      </div>
    </>
  );
}